import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { APIData, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { getDeepActiveElement, isEditableElementActive } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import BlockSettingsComponent from 'webapp/components/block/settings';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import NoDataComponent from 'webapp/components/no-data';

import type BlockState from 'webapp/models-custom/block-state';
import type Board from 'webapp/models-custom/board';
import type BoardState from 'webapp/models-custom/board-state';

import { DesignerTourLocation } from 'webapp/services/flow/tour/designer';

export interface Args {
	boardState: BoardState | null;
	isOpen: boolean;
	onPanelClose: () => void;
}

export default class DesignerRightPanelComponent extends Component<Args> {
	@service('constants') declare constantsService: Service.Constants;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('flow/tour/designer') declare flowTourDesignerService: Service.Flow.Tour.Designer;
	@service('event-manager') declare eventManagerService: Service.EventManager;

	@tracked boardAutoupdateRequestInProgress = false;
	@tracked boardRandomizerRequestInProgress = false;
	@tracked shouldClosePanel = false;

	get board() {
		return (this.args.boardState?.board as Board) || null;
	}

	get selectedBlock() {
		return this.args.boardState?.selectedBlock || null;
	}

	get selectedBlockCopy() {
		const selectedBlockRecord = this.selectedBlock?.model;
		if (selectedBlockRecord) {
			return this.mmBlocksService.createModel(selectedBlockRecord);
		} else {
			return null;
		}
	}

	get selectedBlockState(): BlockState | null {
		return this.args.boardState?.selectedBlockState || null;
	}

	get loggedInAsPowerUser(): boolean {
		return this.currentUserService.loggedInAsPowerUser;
	}

	get isPowerUser(): boolean {
		return this.currentUserService.isPowerUser;
	}

	get isPlatformAdmin(): boolean {
		return this.currentUserService.isPlatformAdmin;
	}

	get isPanelOpen(): boolean {
		return this.args.isOpen && !this.shouldClosePanel;
	}

	bindSpecialActions(): void {
		// enable/disable autoupdate on board
		// CTRL+ALT+a
		$(window).on('keydown.boardAutoupdate', (e) => {
			const allowAdvancedKeypress =
				this.loggedInAsPowerUser || this.isPowerUser || this.isPlatformAdmin;
			if (
				allowAdvancedKeypress &&
				e.ctrlKey &&
				e.altKey &&
				e.keyCode === 65 &&
				!isEditableElementActive()
			) {
				const boardId = this.board?.id;
				if (boardId && !this.boardAutoupdateRequestInProgress) {
					this.boardAutoupdateRequestInProgress = true;
					this.requestService
						.api<APIData.Boards.AutoupdateResponse>(
							'POST',
							`/boards/${boardId}/autoupdate`,
						)
						.then((reply) => {
							this.infolayerService.ok(`AutoUpdate ${reply.on ? 'on' : 'off'}!`);
						})
						.finally(() => {
							this.boardAutoupdateRequestInProgress = false;
						})
						.catch(
							this.requestService.showAllValidationErrors.bind(this.requestService),
						);
				}
			}
		});

		// switch sample / live on board
		// CTRL+ALT+r
		$(window).on('keydown.randomizer', (e) => {
			const allowAdvancedKeypress =
				this.loggedInAsPowerUser || this.isPowerUser || this.isPlatformAdmin;
			if (
				allowAdvancedKeypress &&
				e.ctrlKey &&
				e.altKey &&
				e.keyCode === 82 &&
				!isEditableElementActive()
			) {
				const board = this.board;
				if (board && !this.boardRandomizerRequestInProgress) {
					this.boardRandomizerRequestInProgress = true;
					const layer = this.infolayerService.loading(
							'Turning randomizer ' + (board.isRandom ? 'on' : 'off') + '...',
						),
						startAnimation = function () {
							$('body').addClass('shake');
						},
						endAnimation = function () {
							$('body').removeClass('shake');
						};
					board.isRandom = !board.isRandom;
					startAnimation();
					board
						.save()
						.then(
							() => {
								layer.ok(
									'Randomizer is now ' + (board.isRandom ? 'ON' : 'OFF') + '!',
								);
							},
							() => {
								layer.close();
							},
						)
						.finally(() => {
							this.boardRandomizerRequestInProgress = false;
							endAnimation();
						})
						.catch(
							this.requestService.showAllValidationErrors.bind(this.requestService),
						);
				}
			}
		});
		// enable / disable color on a block
		// CTRL+ALT+o
		$(window).on('keydown.blockcolor', (e) => {
			const allowAdvancedKeypress = this.isPowerUser || this.isPlatformAdmin;
			if (
				allowAdvancedKeypress &&
				e.ctrlKey &&
				e.altKey &&
				e.keyCode === 79 &&
				!isEditableElementActive()
			) {
				const isAlreadyEnabled = this.currentUserService.allowChangingBlockColor;
				this.infolayerService.ok(
					`Block color change ${isAlreadyEnabled ? 'dis' : 'en'}abled!`,
				);
				this.currentUserService.allowChangingBlockColor = !isAlreadyEnabled;
			}
		});

		$(window).on('keydown.deleteBlock', (e) => {
			// eslint-disable-next-line ember/jquery-ember-run
			e.stopPropagation();

			if (
				e.key === 'Delete' ||
				e.keyCode === 46 ||
				e.key === 'Backspace' ||
				e.keyCode === 8
			) {
				const selectedBlock = this.selectedBlock,
					activePopup = this.popupService.activePopup,
					isModalOpen = activePopup?.isModal,
					isTinyMceFocused = !!document.querySelector('.mce-edit-focus');

				if (selectedBlock && !isModalOpen && !isTinyMceFocused) {
					const isDropdownOpen = $('.select2-container--open').length > 0;

					if (!isDropdownOpen) {
						const activeElement = getDeepActiveElement();

						let isInputFocused = false;
						if (activeElement) {
							// A focused text field (e.g. the Genie composer) owns Backspace/Delete;
							// a focused checkbox or radio doesn't, so the prompt still opens
							const activeElTagName = activeElement.tagName.toLowerCase();
							isInputFocused =
								activeElTagName === 'textarea' ||
								(activeElTagName === 'input' &&
									!['checkbox', 'radio'].includes(
										(activeElement as HTMLInputElement).type,
									)) ||
								(activeElement as HTMLElement).isContentEditable;
						}

						if (!isInputFocused) {
							e.preventDefault();
							this.mmBlocksService.promptDeleteFromBoard(selectedBlock);
						}
					}
				}
			}
		});
	}

	unbindSpecialActions(): void {
		$(window).off(
			'keydown.recalculate keydown.boardAutoupdate keydown.randomizer keydown.blockcolor keydown.deleteBlock',
		);
	}

	@action
	closePanel() {
		this.args.onPanelClose();
		this.flowTourDesignerService.endActiveTourAtLocation(DesignerTourLocation.RIGHT_PANEL);
	}

	@action
	setupView(): void {
		this.bindSpecialActions();
		this.setupAiSummaryEditingEvent();
	}

	@action
	teardownView(): void {
		this.unbindSpecialActions();
		this.teardownAiSummaryEditingEvent();
	}

	private setupAiSummaryEditingEvent(): void {
		this.eventManagerService.on(
			AppEvent.AI_SUMMARY_EDITING_STATE_CHANGED,
			this,
			(eventData) => {
				this.shouldClosePanel = eventData.isEditing;
				if (this.shouldClosePanel) {
					this.closePanel();
				}
			},
		);
	}

	private teardownAiSummaryEditingEvent(): void {
		this.eventManagerService.off(AppEvent.AI_SUMMARY_EDITING_STATE_CHANGED, this);
	}
	<template>
		<div
			class="bs-panel designer-panel designer-panel--right
				{{unless @isOpen 'designer-panel--closed'}}"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			<div class="designer-panel__inner">

				<div class="designer-panel__title">
					Datablock editor
					<a role="button" class="close" {{on "click" this.closePanel}}>
						<CommonUiIconComponent @name="Close" @size="sm" />
					</a>
				</div>

				<div class="designer-panel__bg"></div>

				<div class="designer-panel__inner-content">
					{{#if this.selectedBlockCopy}}
						<BlockSettingsComponent
							@model={{this.selectedBlockCopy}}
							@isPanelOpen={{@isOpen}}
						/>
					{{else if this.board.isDataLoading}}
						<div class="bar-container">
							<BarLoaderComponent />
						</div>
					{{else}}
						<NoDataComponent
							@type="greypanel"
							@title="Nothing to display here"
							@text="Select a datablock and use this panel to change the data source, metric, date range, and more."
							@icon="Bulb"
						/>
					{{/if}}
				</div>

			</div>

		</div>
	</template>
}
